import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
    Search, RotateCcw, Loader2, AlertTriangle, TestTube, Ban, Check, Copy,
    Clock, X, ClipboardCheck, Syringe, CalendarDays,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import usePermissions from '../../hooks/usePermissions';
import useSession from '../../hooks/useSession';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { hospitalPatientCode } from '../../utils/patientCode';
import { formatTime } from '../../utils/clock';
import {
    getWorklist, collectSample, receiveSample, rejectSample,
} from '../../redux/labSlice';

// THE PHLEBOTOMY SCREEN. Who is waiting to be bled, one press to say the tube
// exists, and the number to write on it before the patient walks away.
//
// THE WHOLE SCREEN IS BUILT AROUND ONE MOMENT: the accession number.
// Once the patient leaves, the laboratory never sees them again — it sees a
// labelled container. From collection onwards 'L26-000123' IS the patient as far
// as the bench, the analyser worklist and the rejection register are concerned.
// So the number is allocated at the press of the collect button and then thrown
// on the screen at a size somebody can read while holding a tube in one hand and
// a marker in the other. A toast would be the wrong shape for it: a toast
// disappears, and the tube is still blank.
//
// WHY THE NUMBER IS MINTED HERE AND NOT WHEN THE TEST WAS ORDERED
// An order that is never collected must not burn a number out of the year's
// sequence and leave a hole an auditor asks about (models/labSampleModel.js).
// A row on the "to collect" list is therefore a tube that does not exist yet,
// which is also why it has no accession number and cannot be rejected — there is
// nothing to throw away until somebody has drawn it.
//
// WHY THIS IS NOT THE WORKLIST, WHICH ALSO COLLECTS
// LabWorklist covers all five stages a specimen passes through and is the
// screen the bench keeps open all day; collecting is one of the five things it
// can do. This screen is the phlebotomy room's own, and it is shaped by the two
// minutes a patient is actually in the chair: two stages instead of five, one
// press instead of a form (the specimen and container were decided by the test
// master when the test was ordered, and re-typing them at the chair under queue
// pressure is how a serum sample ends up recorded as plasma), and the accession
// number presented as something to copy onto a tube rather than as a
// confirmation to dismiss.
//
// WHY 'IN TRANSIT' IS THE SECOND TAB
// The gap between collectedAt and receivedAt is the pre-analytical delay, and it
// is the single largest source of wrong results in a hospital laboratory —
// potassium leaks out of cells in a tube left on a trolley, glucose falls a few
// percent an hour, a coagulation sample is worthless after four. Same room, same
// person, same shift, so the tubes drawn but not booked in are shown beside the
// ones not yet drawn with the clock running on each. A laboratory that cannot see
// that its ward runs take three hours will never work out why its potassiums
// read high.
//
// A REJECTION IS A CLINICAL EVENT, NOT AN UNDO.
// It discards the specimen and means the patient is stuck again, so the reason is
// mandatory and the row is kept: "how often do we redraw, and from which ward" is
// a quality indicator the laboratory has to report, and the patient asking why
// they were bled twice deserves an answer that is written down. A recollection is
// a NEW tube with a NEW number; nothing here revives the old one.
//
// ---------------------------------------------------------------------------
// THE API THIS SCREEN CONSUMES, AND WHERE IT LIVES.
//
// Not here. Every call goes through redux/labSlice.js — getWorklist,
// collectSample, receiveSample, rejectSample — and this file holds no URL of its
// own. That is the repair of a real defect: this screen and LabWorklist.jsx each
// kept a private copy of the collection path and the two DISAGREED, one posting
// to the order and one to a sample that does not exist until the tube is drawn.
// Two spellings of one endpoint is a screen that 404s on the busiest action in
// this room. One caller is what makes that impossible.
//
// The shapes those thunks return are documented on each of them; the only one
// this screen cannot proceed without is `data.accessionNo` from collectSample.
//
// A row, with every field this file reads:
//   orderId, sampleId, accessionNo, sampleType, containerType, containerColour,
//   collectionSite, priority ('routine'|'urgent'|'stat'), patientId, patientName,
//   patientAge, patientGender, testCount, tests: [{ testId, name }],
//   orderedAt, collectedAt
// Everything except orderId is read defensively: a row missing its container
// renders as "not specified" rather than as a crash on a screen somebody is
// working a queue from.
// ---------------------------------------------------------------------------

// The two stages that happen in this room. The ids are the server's buckets.
const TAB_PENDING = 'pending_collection';
const TAB_COLLECTED = 'collected';

const REFRESH_MS = 30000;

// ----------------------------------------------------------------- time ----

// The time part is the one clock format ("2:30 PM", utils/clock).
const DAY = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' });
const stampOf = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '-' : `${DAY.format(d)}, ${formatTime(d)}`;
};

/** Local calendar day as 'YYYY-MM-DD'. Never via toISOString(), which is UTC and
 *  hands back yesterday for anybody east of Greenwich after teatime — on a
 *  collection list that reads as an empty waiting room. */
const todayLocal = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const minutesSince = (value) => {
    if (!value) return null;
    const then = new Date(value).getTime();
    if (!Number.isFinite(then)) return null;
    return Math.max(0, Math.round((Date.now() - then) / 60000));
};

/** '18m', '3h 20m', '2d 4h' — read at a glance, never a decimal of an hour. */
const elapsedLabel = (mins) => {
    if (mins === null || mins === undefined) return '-';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) {
        const rest = mins % 60;
        return rest ? `${hours}h ${rest}m` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const restH = hours % 24;
    return restH ? `${days}d ${restH}h` : `${days}d`;
};

// These thresholds are clinical, not cosmetic. Two hours on a trolley already
// moves a potassium; four is where a coagulation sample stops being a sample at
// all. Amber and red here mean "this tube's result may be wrong", which is a
// different statement from "this is taking a while".
const TRANSIT_WARN_MINUTES = 120;
const TRANSIT_CRITICAL_MINUTES = 240;

const transitTone = (mins) => {
    if (mins === null || mins === undefined) return 'text-slate-500';
    if (mins >= TRANSIT_CRITICAL_MINUTES) return 'text-red-700 font-bold';
    if (mins >= TRANSIT_WARN_MINUTES) return 'text-amber-700 font-semibold';
    return 'text-slate-600';
};

// A patient sitting in the chair for an hour is a queue problem, not a specimen
// problem, so the waiting column gets louder and never goes red.
const waitTone = (mins) => {
    if (mins === null || mins === undefined) return 'text-slate-500';
    if (mins >= 60) return 'text-amber-700 font-semibold';
    return 'text-slate-600';
};

// --------------------------------------------------------------- labels ----

const SITE_LABELS = {
    lab: 'Lab',
    home: 'Home visit',
    ward: 'Ward',
    otc: 'Collection point',
};
const SITE_STYLES = {
    lab: 'bg-teal-50 text-[#2D8A8A] border-[#4ABDBD]/30',
    // A home collection is hours of transit before the tube is ever booked in,
    // and a ward draw by ward staff is the commonest source of haemolysis. Both
    // are marked so whoever receives the tube knows what they are holding.
    home: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    ward: 'bg-amber-50 text-amber-700 border-amber-200',
    otc: 'bg-gray-100 text-gray-600 border-gray-200',
};

// stat and urgent are the only two that change what anybody does next. 'routine'
// gets no badge at all — a label on every row is a label on no row.
const PRIORITY_STYLES = {
    stat: { badge: 'bg-red-600 text-white', rail: 'border-l-4 border-red-600', label: 'STAT' },
    urgent: { badge: 'bg-amber-500 text-white', rail: 'border-l-4 border-amber-500', label: 'URGENT' },
};

/**
 * The reasons a tube is thrown away, as a laboratory actually says them.
 *
 * Offered as chips because a rejection reason is a quality statistic before it
 * is a sentence: "haemolysed" typed six different ways by six technicians cannot
 * be counted, and counting is most of why the row is kept. Free text stays
 * underneath — the chip fills the box rather than replacing it — because the
 * seventh reason always exists.
 */
const REJECT_REASONS = [
    'Haemolysed',
    'Clotted sample',
    'Insufficient volume',
    'Wrong container',
    'Unlabelled / mislabelled',
    'Leaked in transit',
];

const errorText = (error, fallback) => extractErrorMessage(error, fallback);

// No system id on screen (owner, 29 Sep 2026): a missing name reads "Patient",
// and the only number drawn for a patient is the hospital's own code.
const patientNameOf = (row, t) => row.patientName || t('common.patient_label', 'Patient');

const slug = (value) => String(value).toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '');

/**
 * The tube colour, drawn as a colour.
 *
 * A phlebotomist reaches for lavender or a red top; the word is what the master
 * stores and the dot is what the hand recognises. An unrecognised colour name
 * simply paints nothing — the container's name is printed beside it either way,
 * so the row never depends on the swatch to be readable.
 */
const ContainerSwatch = ({ colour }) => {
    if (!colour) return null;
    return (
        <span
            aria-hidden="true"
            className="inline-block w-2.5 h-2.5 rounded-full border border-gray-300 align-middle mr-1"
            style={{ backgroundColor: String(colour).toLowerCase() }}
        />
    );
};

export default function LabCollection() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const session = useSession();
    const [searchParams] = useSearchParams();

    // WHOSE LABORATORY. Ignored for anybody carrying a hospital in their token —
    // which is every phlebotomist who will ever open this screen — and required
    // for Super Admin, who holds none and is answered 400 until the organisation
    // is named on the URL. Sent on every call so one screen serves both.
    const tenantId = searchParams.get('tenantId') || session.tenantId || '';

    // Drawing, booking in and rejecting are one grant: they are the three things
    // that happen to a tube in this room. Hiding the buttons is the polite half
    // only — api/routes guards the routes, and this screen says so in words
    // rather than letting somebody type into a 403.
    const canCollect = can('lab.sample.collect');

    const [bucket, setBucket] = useState(TAB_PENDING);
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [date, setDate] = useState(() => todayLocal());
    const [refreshKey, setRefreshKey] = useState(0);

    // Which row is mid-request. Held per row rather than as one page-wide flag:
    // a collection list is worked down at speed, and freezing every row because
    // one is saving costs more than it protects.
    const [busyOrderId, setBusyOrderId] = useState(null);
    const [accession, setAccession] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);

    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(id);
    }, [searchQuery]);

    // A search is a hunt for one named person or one accession number, and the
    // tube being hunted is usually the one that did NOT come back today — that
    // is why somebody is searching. So the day is dropped while a term is
    // present, and the screen says so rather than quietly returning nothing.
    const searching = debouncedSearch.length > 0;

    const load = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        try {
            const body = await dispatch(getWorklist({
                bucket,
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearch,
                // Dropped while a term is present — see above on why the hunt is
                // usually for the tube that did NOT come back today. The slice
                // omits an empty value rather than sending `?date=`.
                date: debouncedSearch ? '' : date,
                tenantId,
            })).unwrap();
            setRows(body?.data || []);
            setCounts(body?.counts || {});
            setTotal(Number(body?.total) || 0);
            setLoadError(null);
        } catch (error) {
            setRows([]);
            setTotal(0);
            setLoadError(errorText(error, t('labcollection.load_error', 'Could not load the collection list')));
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [dispatch, bucket, currentPage, itemsPerPage, debouncedSearch, date, tenantId, t]);

    useEffect(() => { load(); }, [load, refreshKey]);

    // Two phlebotomists routinely work one queue. The list therefore refreshes
    // itself quietly — no spinner, because a screen that blinks every half
    // minute is a screen people stop reading — and pauses while a dialog is open
    // so a row cannot be swapped out from under somebody confirming a tube. The
    // pause lives in a ref so the interval reads the current answer instead of
    // being torn down and rebuilt on every state change, which is how a poll
    // ends up never firing.
    const pausedRef = useRef(false);
    useEffect(() => {
        pausedRef.current = !!accession || !!rejectTarget || !!busyOrderId;
    }, [accession, rejectTarget, busyOrderId]);
    useEffect(() => {
        const id = setInterval(() => {
            if (!pausedRef.current) load({ quiet: true });
        }, REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

    const switchTab = (next) => {
        if (next === bucket) return;
        setBucket(next);
        // The two lists have nothing in common but their length, so the old page
        // number is meaningless against the new one.
        setCurrentPage(1);
        setRows([]);
    };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    /**
     * The tube now exists. ONE PRESS — no form.
     *
     * Everything a form would ask is already on the row: the test master decided
     * the specimen and the container when the test was ordered, and the site was
     * decided when the visit was booked. Asking again puts a modal between a
     * phlebotomist and a patient with a needle in their arm, and the answers get
     * re-typed wrong under queue pressure. They are sent back explicitly rather
     * than left to the server's defaults so the specimen row records what this
     * screen actually showed the person who drew it. The one thing that CANNOT
     * be got wrong — the accession number — is what the press produces, so that
     * is what fills the screen afterwards.
     */
    const handleCollect = async (row) => {
        setBusyOrderId(row.orderId);
        try {
            const payload = await dispatch(collectSample({
                orderId: row.orderId,
                sampleType: row.sampleType || null,
                containerType: row.containerType || null,
                collectionSite: row.collectionSite || 'lab',
                tenantId,
            })).unwrap();
            const body = payload?.data || payload || {};
            const accessionNo = body.accessionNo;
            if (!accessionNo) {
                // A collected tube whose number never reached the screen is an
                // unlabelled tube, which is a rejection and a second needle.
                throw new Error(t('labcollection.no_accession', 'The collection was recorded but no accession number came back — find it on the sample before labelling the tube.'));
            }
            setAccession({ ...row, ...body, accessionNo });
            refetch();
        } catch (error) {
            toast.warn(errorText(error, t('labcollection.collect_error', 'Could not record the collection')));
        } finally {
            setBusyOrderId(null);
        }
    };

    /**
     * Booked in at the bench. Separate from the draw even when the same person
     * does both a metre apart, because this is the moment the laboratory takes
     * over the turnaround clock — and because the gap between the two is the
     * pre-analytical delay this screen exists to make visible.
     */
    const handleReceive = async (row) => {
        if (!row.sampleId) return;
        setBusyOrderId(row.orderId);
        try {
            await dispatch(receiveSample({ sampleId: row.sampleId, tenantId })).unwrap();
            toast.success(row.accessionNo ? `${t('labcollection.received', 'Booked in')}: ${row.accessionNo}` : t('labcollection.received', 'Booked in'));
            refetch();
        } catch (error) {
            toast.warn(errorText(error, t('labcollection.receive_error', 'Could not book the sample in')));
        } finally {
            setBusyOrderId(null);
        }
    };

    const waiting = bucket === TAB_PENDING;
    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    // The worst case on the page, not the average — an average hides the one
    // tube that has been in a bag since the morning ward round.
    const worstElapsed = useMemo(() => {
        const field = waiting ? 'orderedAt' : 'collectedAt';
        const values = rows.map((r) => minutesSince(r[field])).filter((n) => n !== null);
        return values.length ? Math.max(...values) : null;
    }, [rows, waiting]);

    const TABS = [
        { id: TAB_PENDING, icon: Syringe, label: t('labcollection.tab_pending', 'To collect') },
        { id: TAB_COLLECTED, icon: TestTube, label: t('labcollection.tab_collected', 'In transit') },
    ];

    return (
        <div data-testid="lab-collection-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="lab-collection-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('labcollection.title', 'Sample collection')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('labcollection.subtitle', 'Who is waiting to be bled, and what has been drawn but not yet booked in. Collecting allocates the accession number the whole laboratory will know this patient by.')}
                    </p>
                </div>
                <button
                    data-testid="lab-collection-refresh"
                    onClick={refetch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 whitespace-nowrap ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    <RotateCcw className="w-4 h-4" />
                    {t('labcollection.refresh', 'Refresh')}
                </button>
            </div>

            {/* ---------------------------------------------------- tabs ---- */}
            <div className="mb-4 flex flex-col lg:flex-row gap-2 lg:items-center">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    {TABS.map((tab) => {
                        const active = bucket === tab.id;
                        const n = counts[tab.id];
                        return (
                            <button
                                key={tab.id}
                                data-testid={`collection-tab-${tab.id}`}
                                onClick={() => switchTab(tab.id)}
                                className={`relative px-5 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 whitespace-nowrap ${active ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                            >
                                {active && (
                                    <motion.div
                                        layoutId="activeCollectionTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20 inline-flex items-center gap-2">
                                    <tab.icon className="w-4 h-4" />
                                    {tab.label}
                                    {/* The count comes from the server for BOTH
                                        tabs, so the other one is honest while you
                                        are standing on this one. */}
                                    {Number.isFinite(Number(n)) && (
                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                            {n}
                                        </span>
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="relative w-full lg:w-80">
                    <input
                        data-testid="collection-search-input"
                        type="text"
                        // A barcode scanner is a keyboard: it types the label and
                        // presses Enter, which lands here like anything else.
                        placeholder={t('labcollection.search_placeholder', 'Scan a label, or type a name / accession / order…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 50))}
                        maxLength={50}
                        className="w-full p-2 pl-10 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <div className="relative w-full lg:w-52">
                    <input
                        data-testid="collection-date"
                        type="date"
                        value={date}
                        disabled={searching}
                        onChange={(e) => { setDate(e.target.value); setCurrentPage(1); }}
                        className={`w-full p-2 pl-9 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${searching ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    <CalendarDays className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>
            </div>

            {/* Clearing the date is a real answer, not an empty field: yesterday's
                uncollected tube is the row that matters most on this screen, and a
                day filter is exactly how it stops being shown. */}
            <p className="text-[11px] text-slate-400 mb-4">
                {searching
                    ? t('labcollection.searching_note', 'Searching across every day — the date is ignored while there is a search term.')
                    : date
                        ? t('labcollection.date_note', 'Showing this day only. Clear the date to see everything still outstanding, including tubes ordered earlier in the week.')
                        : t('labcollection.no_date_note', 'Showing every outstanding tube, oldest first.')}
            </p>

            {/* ------------------------------------------------- headline ---- */}
            <div className="mb-4 flex flex-wrap gap-3">
                <div className="px-3 py-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 border border-[#4ABDBD]/20">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                        {waiting
                            ? t('labcollection.count_pending', 'Tubes to draw')
                            : t('labcollection.count_transit', 'Drawn, not booked in')}
                    </div>
                    <div className="text-lg font-bold text-[#2D8A8A]" data-testid="collection-count">{total}</div>
                </div>
                {worstElapsed !== null && (
                    <div className={`px-3 py-2 rounded-xl border ${!waiting && worstElapsed >= TRANSIT_WARN_MINUTES
                        ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'
                        : 'bg-gray-50 border-gray-200'}`}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                            {waiting
                                ? t('labcollection.longest_wait', 'Longest wait')
                                : t('labcollection.longest_transit', 'Longest since draw')}
                        </div>
                        <div className={`text-lg font-bold ${waiting ? waitTone(worstElapsed) : transitTone(worstElapsed)}`} data-testid="collection-worst-elapsed">
                            {elapsedLabel(worstElapsed)}
                        </div>
                    </div>
                )}
            </div>

            {/* The pre-analytical warning, said once and in full, on the tab where
                somebody can still act on it. */}
            {!waiting && worstElapsed !== null && worstElapsed >= TRANSIT_WARN_MINUTES && (
                <div data-testid="transit-warning" className="mb-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                        {t('labcollection.transit_warning', 'A tube on this list has been out for over two hours. Potassium rises and glucose falls while a specimen sits, and a coagulation sample is not usable after four — book them in now, or reject and redraw rather than running them.')}
                    </span>
                </div>
            )}

            {!canCollect && (
                <div data-testid="collection-readonly-note" className="mb-4 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
                    {t('labcollection.readonly', 'You can read the collection lists but not act on them — drawing, booking in and rejecting a tube all need the sample-collection permission (lab.sample.collect).')}
                </div>
            )}

            {loadError && (
                <div data-testid="collection-load-error" className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {loadError}
                </div>
            )}

            {/* ------------------------------------------------ the list ---- */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('labcollection.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="collection-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labcollection.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labcollection.patient', 'Patient')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labcollection.tests', 'Tests')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labcollection.specimen', 'Specimen')}</th>
                                    {!waiting && (
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labcollection.accession', 'Accession')}</th>
                                    )}
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">
                                        {waiting ? t('labcollection.waiting', 'Waiting') : t('labcollection.since_draw', 'Since draw')}
                                    </th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('labcollection.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="collection-table-body">
                                {rows.length > 0 ? rows.map((row, index) => {
                                    const elapsed = minutesSince(waiting ? row.orderedAt : row.collectedAt);
                                    const busy = busyOrderId === row.orderId;
                                    const priority = PRIORITY_STYLES[row.priority] || null;
                                    const late = !waiting && elapsed !== null && elapsed >= TRANSIT_CRITICAL_MINUTES;
                                    return (
                                        <tr
                                            key={row.orderId}
                                            data-testid={`collection-row-${row.orderId}`}
                                            className={`border-b border-gray-100/50 transition-all duration-300 ${priority ? priority.rail : ''} ${late
                                                ? 'bg-red-50/50 hover:bg-red-50'
                                                : 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80'}`}
                                        >
                                            <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                            <td className="p-3 text-xs">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-gray-700">{patientNameOf(row, t)}</span>
                                                    {priority && (
                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${priority.badge}`}>
                                                            {priority.label}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-slate-400">
                                                    {[row.patientAge, row.patientGender].filter(Boolean).join(' · ')}
                                                </div>
                                                {hospitalPatientCode(row) && <div className="text-[11px] text-slate-400 font-mono">{hospitalPatientCode(row)}</div>}
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">
                                                <TestSummary row={row} t={t} />
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">
                                                {/* Specimen and container as the test master
                                                    specified them. This is what the phlebotomist
                                                    reaches for — the wrong colour top is a
                                                    rejection and a second needle. */}
                                                <div className="capitalize font-semibold text-gray-700">{row.sampleType || '-'}</div>
                                                <div className="text-[11px] text-slate-400">
                                                    <ContainerSwatch colour={row.containerColour} />
                                                    {row.containerType || t('labcollection.no_container', 'container not specified')}
                                                </div>
                                                <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${SITE_STYLES[row.collectionSite] || SITE_STYLES.otc}`}>
                                                    {t(`labcollection.site_${row.collectionSite}`, SITE_LABELS[row.collectionSite] || row.collectionSite || 'Lab')}
                                                </span>
                                            </td>
                                            {!waiting && (
                                                <td className="p-3 text-xs font-mono font-semibold text-gray-800">
                                                    {row.accessionNo || t('labcollection.not_drawn', 'not drawn')}
                                                </td>
                                            )}
                                            <td className="p-3 text-xs">
                                                <div className={`inline-flex items-center gap-1 ${waiting ? waitTone(elapsed) : transitTone(elapsed)}`}>
                                                    <Clock className="w-3 h-3" />
                                                    {elapsedLabel(elapsed)}
                                                </div>
                                                <div className="text-[11px] text-slate-400">
                                                    {stampOf(waiting ? row.orderedAt : row.collectedAt)}
                                                </div>
                                            </td>
                                            <td className="p-3 text-xs">
                                                <div className="flex justify-center gap-2">
                                                    {canCollect && waiting && (
                                                        <button
                                                            data-testid={`collect-${row.orderId}`}
                                                            onClick={() => handleCollect(row)}
                                                            disabled={busy}
                                                            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-white text-xs font-semibold bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow-md transition-all duration-300 ${busy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-lg hover:scale-105'}`}
                                                        >
                                                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Syringe className="w-4 h-4" />}
                                                            {t('labcollection.collect', 'Collect')}
                                                        </button>
                                                    )}
                                                    {canCollect && !waiting && (
                                                        <>
                                                            <button
                                                                data-testid={`receive-${row.orderId}`}
                                                                onClick={() => handleReceive(row)}
                                                                disabled={busy || !row.sampleId}
                                                                title={t('labcollection.receive_hint', 'Book in at the bench — the turnaround clock starts here')}
                                                                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-white text-xs font-semibold bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow-md transition-all duration-300 ${busy || !row.sampleId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-lg'}`}
                                                            >
                                                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
                                                                {t('labcollection.receive', 'Book in')}
                                                            </button>
                                                            <button
                                                                data-testid={`reject-${row.orderId}`}
                                                                onClick={() => setRejectTarget(row)}
                                                                disabled={busy || !row.sampleId}
                                                                title={t('labcollection.reject_hint', 'Discard this tube — the patient will have to be drawn again')}
                                                                className={`text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md transition-all duration-300 hover:scale-110 ${busy || !row.sampleId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                            >
                                                                <Ban className="h-4 w-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                    {/* No reject on the left-hand tab, and it is
                                                        not an oversight: there is no tube yet to
                                                        throw away. An order that should not be
                                                        drawn is cancelled on the order, not
                                                        rejected here. */}
                                                    {!canCollect && (
                                                        <span className="text-[11px] text-slate-400">
                                                            {t('labcollection.locked', 'read only')}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={waiting ? 6 : 7} className="p-6 text-center text-gray-500">
                                            {waiting
                                                ? t('labcollection.empty_pending', 'Nobody is waiting. A row appears here the moment a doctor orders a test.')
                                                : t('labcollection.empty_transit', 'Nothing in transit — every tube drawn has been booked in at the bench.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards below lg. A phlebotomy trolley carries a tablet or a
                        phone far more often than a desktop, and this is the one
                        screen in the module genuinely used standing up. */}
                    <div className="block lg:hidden space-y-4">
                        {rows.length > 0 ? rows.map((row) => {
                            const elapsed = minutesSince(waiting ? row.orderedAt : row.collectedAt);
                            const busy = busyOrderId === row.orderId;
                            const priority = PRIORITY_STYLES[row.priority] || null;
                            const late = !waiting && elapsed !== null && elapsed >= TRANSIT_CRITICAL_MINUTES;
                            return (
                                <div
                                    key={row.orderId}
                                    data-testid={`collection-card-${row.orderId}`}
                                    className={`p-4 rounded-xl shadow-md ${priority ? priority.rail : ''} ${late ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-gray-800 text-sm">{patientNameOf(row, t)}</span>
                                                {priority && (
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${priority.badge}`}>
                                                        {priority.label}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-slate-400">
                                                {[row.patientAge, row.patientGender].filter(Boolean).join(' · ')}
                                            </div>
                                            {hospitalPatientCode(row) && <div className="text-[11px] text-slate-400 font-mono">{hospitalPatientCode(row)}</div>}
                                        </div>
                                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold ${SITE_STYLES[row.collectionSite] || SITE_STYLES.otc}`}>
                                            {t(`labcollection.site_${row.collectionSite}`, SITE_LABELS[row.collectionSite] || row.collectionSite || 'Lab')}
                                        </span>
                                    </div>

                                    <div className="mt-2 text-xs text-gray-600">
                                        <TestSummary row={row} t={t} />
                                    </div>

                                    <div className="mt-2 text-xs text-gray-600">
                                        <span className="capitalize font-semibold">{row.sampleType || '-'}</span>
                                        {row.containerType ? (
                                            <span className="text-slate-500"> · <ContainerSwatch colour={row.containerColour} />{row.containerType}</span>
                                        ) : null}
                                    </div>

                                    {!waiting && (
                                        <div className="mt-2 font-mono text-sm font-bold text-gray-800">
                                            {row.accessionNo || t('labcollection.not_drawn', 'not drawn')}
                                        </div>
                                    )}

                                    <div className={`mt-2 inline-flex items-center gap-1 text-[11px] ${waiting ? waitTone(elapsed) : transitTone(elapsed)}`}>
                                        <Clock className="w-3 h-3" />
                                        {elapsedLabel(elapsed)}
                                        <span className="text-slate-400">· {stampOf(waiting ? row.orderedAt : row.collectedAt)}</span>
                                    </div>

                                    {canCollect && (
                                        <div className="flex gap-2 mt-3">
                                            {waiting ? (
                                                <button
                                                    onClick={() => handleCollect(row)}
                                                    disabled={busy}
                                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-xs font-semibold bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow ${busy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                >
                                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Syringe className="w-4 h-4" />}
                                                    {t('labcollection.collect', 'Collect')}
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => handleReceive(row)}
                                                        disabled={busy || !row.sampleId}
                                                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-xs font-semibold bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow ${busy || !row.sampleId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                    >
                                                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
                                                        {t('labcollection.receive', 'Book in')}
                                                    </button>
                                                    <button
                                                        onClick={() => setRejectTarget(row)}
                                                        disabled={busy || !row.sampleId}
                                                        className={`px-4 py-2.5 rounded-xl border border-red-200 text-red-600 text-xs font-semibold ${busy || !row.sampleId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                    >
                                                        {t('labcollection.reject', 'Reject')}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        }) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {waiting
                                    ? t('labcollection.empty_pending', 'Nobody is waiting. A row appears here the moment a doctor orders a test.')
                                    : t('labcollection.empty_transit', 'Nothing in transit — every tube drawn has been booked in at the bench.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!loading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <AccessionDialog
                sample={accession}
                onClose={() => setAccession(null)}
                t={t}
            />

            <RejectDialog
                row={rejectTarget}
                tenantId={tenantId}
                onClose={() => setRejectTarget(null)}
                onRejected={() => { setRejectTarget(null); refetch(); }}
                t={t}
            />
        </div>
    );
}

/**
 * What is being drawn for. A count alone ("4 tests") does not tell a
 * phlebotomist whether they need one tube or three, so the names are listed and
 * only the tail is collapsed.
 */
const TestSummary = ({ row, t }) => {
    const tests = Array.isArray(row.tests) ? row.tests : [];
    const count = Number(row.testCount) || tests.length;
    if (!tests.length) {
        return <span className="text-slate-400">{count ? `${count} ${t('labcollection.tests_lower', 'tests')}` : '-'}</span>;
    }
    const shown = tests.slice(0, 3);
    const rest = Math.max(0, count - shown.length);
    return (
        <div className="flex flex-wrap gap-1">
            {shown.map((test) => (
                <span key={test.testId || test.name} className="px-2 py-0.5 rounded bg-gray-100 text-[11px] text-gray-600">
                    {test.name || '—'}
                </span>
            ))}
            {rest > 0 && <span className="px-2 py-0.5 rounded bg-gray-100 text-[11px] text-gray-500">+{rest}</span>}
        </div>
    );
};

/* ==================================================================== *
 * The number to write on the tube.
 * ==================================================================== */

/**
 * Deliberately enormous, deliberately monospaced, deliberately in the way.
 *
 * This dialog exists because of one failure: a tube labelled with the wrong
 * number, or with none — the classic laboratory catastrophe, a result filed
 * against another patient. It is modal because the label has to be written
 * before the next patient sits down; it is monospaced because 'L26-000123' is
 * copied digit by digit and a proportional font makes 1 and 7, 0 and O argue;
 * and the patient's name sits underneath because a number cannot be checked
 * against the person it came out of.
 */
function AccessionDialog({ sample, onClose, t }) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!sample) return undefined;
        setCopied(false);
        // Enter as well as Escape: the phlebotomist's hands are busy and the
        // dialog is a checkpoint, not a question.
        const onKey = (e) => { if (e.key === 'Escape' || e.key === 'Enter') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [sample, onClose]);

    if (!sample) return null;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(sample.accessionNo);
            setCopied(true);
        } catch {
            // Clipboard access is blocked on plain HTTP and inside some kiosk
            // browsers. The number is on the screen at 48px; failing to copy it
            // does not deserve an error that covers it up.
            setCopied(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" data-testid="accession-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
                <div className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-semibold">{t('labcollection.collected_title', 'Collected — label the tube')}</span>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 cursor-pointer" aria-label={t('labcollection.close', 'Close')}>
                        <X className="w-4 h-4 text-white" />
                    </button>
                </div>

                <div className="p-6 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                        {t('labcollection.accession_label', 'Write this on the tube')}
                    </div>
                    <div
                        data-testid="accession-number"
                        className="font-mono text-4xl sm:text-5xl font-bold tracking-wider text-[#2D8A8A] break-all select-all"
                    >
                        {sample.accessionNo}
                    </div>

                    <button
                        data-testid="accession-copy"
                        onClick={copy}
                        className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-300 text-xs font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer"
                    >
                        <Copy className="w-3.5 h-3.5" />
                        {copied ? t('labcollection.copied', 'Copied') : t('labcollection.copy', 'Copy')}
                    </button>

                    <div className="mt-5 rounded-xl bg-gray-50 border border-gray-200 p-3 text-left">
                        <div className="text-sm font-semibold text-gray-800">{patientNameOf(sample, t)}</div>
                        {hospitalPatientCode(sample) && <div className="text-[11px] text-slate-400 font-mono">{hospitalPatientCode(sample)}</div>}
                        <div className="text-xs text-gray-600 mt-1 capitalize">
                            {sample.sampleType || '-'}
                            {sample.containerType ? (
                                <span className="normal-case"> · <ContainerSwatch colour={sample.containerColour} />{sample.containerType}</span>
                            ) : null}
                        </div>
                    </div>

                    {/* The check that catches the catastrophe. Reading the name
                        back while the tube is still in your hand is the only step
                        that finds a mislabel before the bench does — and the bench
                        cannot find it at all. */}
                    <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                        {t('labcollection.verify_note', 'Read the name back to the patient before the tube leaves your hand. From here on, the laboratory knows them only by this number.')}
                    </p>

                    <button
                        data-testid="accession-done"
                        onClick={onClose}
                        className="mt-4 w-full py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow-lg hover:shadow-xl cursor-pointer"
                    >
                        {t('labcollection.labelled', 'Labelled — next patient')}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

/* ==================================================================== *
 * Reject — a tube thrown away, and the reason it was.
 * ==================================================================== */

function RejectDialog({ row, tenantId, onClose, onRejected, t }) {
    const dispatch = useDispatch();
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (row) setReason('');
    }, [row]);

    if (!row) return null;

    const trimmed = reason.trim();
    // Four characters is a typo check, not a quality check. What makes the
    // reasons countable is the chip list above the box, and what makes them
    // useful is that somebody had to choose one.
    const canSubmit = trimmed.length >= 4 && !saving;

    const submit = async () => {
        if (!canSubmit) return;
        setSaving(true);
        try {
            await dispatch(rejectSample({ sampleId: row.sampleId, rejectionReason: trimmed, tenantId })).unwrap();
            toast.success(row.accessionNo ? `${t('labcollection.rejected', 'Sample rejected — a fresh draw is needed')}: ${row.accessionNo}` : t('labcollection.rejected', 'Sample rejected — a fresh draw is needed'));
            onRejected();
        } catch (error) {
            toast.warn(errorText(error, t('labcollection.reject_error', 'Could not reject the sample')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="reject-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5"
            >
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{t('labcollection.reject_title', 'Reject this sample')}</h2>
                        <p className="text-[11px] text-slate-400">
                            {patientNameOf(row, t)}
                            {row.accessionNo ? <span className="font-mono"> · {row.accessionNo}</span> : null}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 cursor-pointer" aria-label={t('labcollection.close', 'Close')}>
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>

                {/* Said before the reason is asked for, because the person pressing
                    this is often the person who has to go back to the patient —
                    and because a rejection cannot be undone. */}
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 mb-4" data-testid="reject-notice">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] leading-relaxed text-red-800">
                            {t('labcollection.reject_notice', 'The tube is discarded and the patient has to be drawn again. This row is kept with your reason on it — it is how the laboratory counts redraws, and how anybody answers the patient who asks why. A recollection is a new tube with a new number; it never revives this one.')}
                        </p>
                    </div>
                </div>

                <div className="mb-3">
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                        {t('labcollection.reject_reason', 'Why is it being rejected?')}
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {REJECT_REASONS.map((r) => (
                            <button
                                key={r}
                                type="button"
                                data-testid={`reject-reason-${slug(r)}`}
                                onClick={() => setReason(r)}
                                className={`px-3 py-1.5 rounded-xl text-[11px] font-semibold border transition-all cursor-pointer ${trimmed === r
                                    ? 'bg-red-500 text-white border-transparent shadow'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-red-300'}`}
                            >
                                {t(`labcollection.reason_${slug(r).replace(/-/g, '_')}`, r)}
                            </button>
                        ))}
                    </div>
                    <textarea
                        data-testid="reject-reason-input"
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value.slice(0, 200))}
                        maxLength={200}
                        placeholder={t('labcollection.reason_placeholder', 'Pick one above, or describe what was wrong with the specimen…')}
                        className="w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer"
                    >
                        {t('labcollection.keep', 'Keep the sample')}
                    </button>
                    <button
                        data-testid="reject-submit"
                        onClick={submit}
                        disabled={!canSubmit}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-red-500 to-pink-500 shadow-lg hover:shadow-xl ${canSubmit ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                        {t('labcollection.reject_confirm', 'Reject and record')}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
